import { useState, useEffect } from 'react';
import { Link, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Login from './components/login';
import Register from './components/register';
import Todo from './components/todo';
import ResetPassword from './components/forget'; // if you have it
import EmailVerify from './components/emailverify';
import EmailSent from './components/emailsent';
import Settings from './components/settings';

function AppHeader({ user, onLogout }) {
  const location = useLocation();
  const currentPath = location.pathname.replace(/\/$/, '');

  return (
    <header className="app-header">
      <div className="app-header__brand">
        <span className="app-header__eyebrow">Task board</span>
        <div className="app-header__title">To-Do</div>
      </div>

      <nav className="app-header__nav" aria-label="Primary navigation">
        <Link
          to="/todo"
          className={`app-header__link ${currentPath === '/todo' ? 'is-active' : ''}`}
        >
          To-Do page
        </Link>
        <Link
          to="/settings"
          className={`app-header__link ${currentPath === '/settings' ? 'is-active' : ''}`}
        >
          Settings
        </Link>
        {user && (
          <button type="button" className="app-header__link app-header__logout" onClick={onLogout}>
            Log out
          </button>
        )}
      </nav>
    </header>
  );
}

function AppShell({ user, onLogout, children }) {
  return (
    <div className="app-shell">
      <AppHeader user={user} onLogout={onLogout} />
      <main className="app-shell__main">
        <div className="app-shell__page">{children}</div>
      </main>
    </div>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loadingUser, setLoadingUser] = useState(true);
  const [theme, setTheme] = useState(() => localStorage.getItem('theme') || 'light');
  const location = useLocation();

  const handleLogin = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData)); // Save to localStorage
    setUser(userData);
  };

  const handleUserUpdate = (nextUserData) => {
    localStorage.setItem('user', JSON.stringify(nextUserData));
    setUser(nextUserData);
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    const checkUserWithDelay = async () => {
      await new Promise((resolve) => setTimeout(resolve, 1000)); // 3 second delay
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsed = JSON.parse(storedUser);
        // Make local demo account an admin in-session so admin tools are available
        // (only in development/local envs). In production this will not be set.
        try {
          if (
            import.meta.env.DEV &&
            parsed &&
            parsed.data &&
            String(parsed.data.username).toLowerCase() === 'demo'
          ) {
            parsed.data.is_admin = 1;
            localStorage.setItem('user', JSON.stringify(parsed));
          }
        } catch (e) {
          // ignore parse errors
        }
        setUser(parsed);
      }
      setLoadingUser(false); // done after delay
    };

    checkUserWithDelay();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('user');
    setUser(null);
  };

  const toggleTheme = () => {
    setTheme((currentTheme) => (currentTheme === 'dark' ? 'light' : 'dark'));
  };

  if (loadingUser)
    return (
      <div
        style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          height: '100vh',
          width: '100vw',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          color: 'white',
          fontSize: '1.2rem',
        }}
      >
        <div className="spinner"></div>
        <div style={{ marginTop: '1rem' }}>Loading Data...</div>
      </div>
    );

  const shouldShowShell = ['/todo', '/settings'].includes(location.pathname.replace(/\/$/, ''));

  const routes = (
    <Routes>
      <Route
        path="/"
        element={user ? <Navigate to="/todo" replace /> : <Navigate to="/login" replace />}
      />
      <Route
        path="/login"
        element={
          user ? (
            user.data.verified ? (
              <Navigate to="/todo" replace />
            ) : (
              <Navigate to="/emailsent" replace />
            )
          ) : (
            <Login onLogin={handleLogin} />
          )
        }
      />
      <Route
        path="/register"
        element={
          user ? (
            user.data.verified ? (
              <Navigate to="/todo" replace />
            ) : (
              <Navigate to="/emailsent" replace />
            )
          ) : (
            <Register onRegister={handleLogin} />
          )
        }
      />
      <Route
        path="/todo"
        element={
          user ? (
            user.data.verified ? (
              <Todo user={user} onLogout={handleLogout} />
            ) : (
              <Navigate to="/emailsent" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route
        path="/settings"
        element={
          user ? (
            user.data.verified ? (
              <Settings
                user={user}
                theme={theme}
                onToggleTheme={toggleTheme}
                onUserUpdate={handleUserUpdate}
              />
            ) : (
              <Navigate to="/emailsent" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
      <Route path="/forget/" element={<ResetPassword />} />
      <Route path="/emailverify/" element={<EmailVerify />} />
      <Route
        path="/emailsent/"
        element={
          user ? (
            <EmailSent
              email={user.data.email}
              username={user.data.username}
              onVerify={handleLogin}
            />
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />
    </Routes>
  );

  return (
    <div className="app-container">
      {shouldShowShell ? (
        <AppShell user={user} onLogout={handleLogout}>
          {routes}
        </AppShell>
      ) : (
        routes
      )}
    </div>
  );
}

export default App;
